import { PERMISSIONS } from '@/constants/permissions'
import type { HasPermissionFn } from '@/utils/navigationPermissionAccess'

/** Re-export for callers that sync DD permit from auth (e.g. ddQueueStore). */
export type { HasPermissionFn }

/** RBAC-SLICE-3 — exact SW/DD gates (no legacy umbrella). */

export const SW_TILE_VIEW_PERMISSIONS = [
  PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.VIEW,
  PERMISSIONS.SALES.WORKFLOW_MODIFY.VIEW,
  PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.VIEW,
  PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.VIEW,
] as const

export const SW_TILE_CREATE_PERMISSIONS = [
  PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.CREATE,
  PERMISSIONS.SALES.WORKFLOW_MODIFY.CREATE,
  PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.CREATE,
  PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.CREATE,
] as const

export const SW_SUBMITTED_VIEW = PERMISSIONS.SALES.WORKFLOW_SUBMITTED.VIEW

export const DD_QUEUE_VIEW = PERMISSIONS.PRODUCTS_DESIGN.QUEUE.VIEW
export const DD_SUPERVISOR_VIEW = PERMISSIONS.PRODUCTS_DESIGN.SUPERVISOR.VIEW

/** My Drafts list gate — any intake tile create. */
export function canAccessMyDrafts(hasPermission: HasPermissionFn): boolean {
  return SW_TILE_CREATE_PERMISSIONS.some((permission) => hasPermission(permission))
}

export function canViewSubmitted(hasPermission: HasPermissionFn): boolean {
  return hasPermission(SW_SUBMITTED_VIEW)
}

export function canViewTile(
  hasPermission: HasPermissionFn,
  tileViewPermission: string,
): boolean {
  return hasPermission(tileViewPermission)
}

export function canAccessAnySalesWorkflow(hasPermission: HasPermissionFn): boolean {
  return (
    SW_TILE_VIEW_PERMISSIONS.some((p) => hasPermission(p)) ||
    canAccessMyDrafts(hasPermission) ||
    canViewSubmitted(hasPermission)
  )
}

/** Queue + Dashboard + History + Cancelled. */
export function canAccessDdQueuePages(hasPermission: HasPermissionFn): boolean {
  return hasPermission(DD_QUEUE_VIEW)
}

export function canAccessDdWorkload(hasPermission: HasPermissionFn): boolean {
  return hasPermission(DD_SUPERVISOR_VIEW)
}

export function canAccessAnyDesignDevelopment(hasPermission: HasPermissionFn): boolean {
  return canAccessDdQueuePages(hasPermission) || canAccessDdWorkload(hasPermission)
}

export function resolveDdPermitFromPermissions(
  hasPermission: HasPermissionFn,
): 'supervisor' | 'user' {
  return canAccessDdWorkload(hasPermission) ? 'supervisor' : 'user'
}

export const SALES_WORKFLOW_ROUTE_PERMISSION_BY_PATH = {
  '/sales/workflow/new-product': PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.VIEW,
  '/sales/workflow/modify': PERMISSIONS.SALES.WORKFLOW_MODIFY.VIEW,
  '/sales/workflow/product-bundle': PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.VIEW,
  '/sales/workflow/new-customer-order': PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.VIEW,
  '/sales/workflow/submitted': SW_SUBMITTED_VIEW,
} as const

/** Drafts use requiredPermissions = SW_TILE_CREATE_PERMISSIONS (any). */
export const SALES_WORKFLOW_DRAFTS_PATH = '/sales/workflow/drafts'

export const DD_ROUTE_PERMISSION_BY_PATH = {
  '/design-development/queue': DD_QUEUE_VIEW,
  '/design-development/dashboard': DD_QUEUE_VIEW,
  '/design-development/history': DD_QUEUE_VIEW,
  '/design-development/cancelled': DD_QUEUE_VIEW,
  '/design-development/workload': DD_SUPERVISOR_VIEW,
} as const

/** Sidebar order — first match wins for default redirects. */
export const DD_NAV_ROUTE_TARGETS = [
  { path: '/design-development/queue', routeName: 'DdQueue' },
  { path: '/design-development/workload', routeName: 'DdWorkload' },
  { path: '/design-development/dashboard', routeName: 'DdDashboard' },
  { path: '/design-development/history', routeName: 'DdHistory' },
  { path: '/design-development/cancelled', routeName: 'DdCancelled' },
] as const

/** Ingest FAB + fixture loaders — DEV-only and super.admin (RBAC-SLICE-2-FIX D3). */
export function canShowDdIngestPanel(hasPermission: HasPermissionFn): boolean {
  return import.meta.env.DEV && hasPermission(PERMISSIONS.SYSTEM.SUPER_ADMIN)
}
